import { useEffect } from "react";
import { motion } from "framer-motion";
import { tr } from "@/lib/i18n";
import { sfx } from "@/lib/audio";
import { useTypewriter } from "@/lib/hooks";
import { CUSTOMERS, grumpyPortrait, bgSrc } from "@/lib/customers";
import { AnswerTile } from "./AnswerTile";

const HEART_ROWS = ["011000110", "111101111", "111111111", "111111111", "011111110", "001111100", "000111000", "000010000"];

const PixelHeart = ({ size }) => (
  <svg viewBox="0 0 9 8" shapeRendering="crispEdges" style={{ width: size, height: "auto", display: "block",
    filter: "drop-shadow(1px 0 0 #4a0d1a) drop-shadow(-1px 0 0 #4a0d1a) drop-shadow(0 1px 0 #4a0d1a) drop-shadow(0 -1px 0 #4a0d1a)" }}>
    {HEART_ROWS.map((row, y) => [...row].map((c, x) => (c === "1" ? <rect key={`${x}-${y}`} x={x} y={y} width="1.02" height="1.02" fill={y < 3 ? "#ff6b81" : "#ff3d63"} /> : null)))}
  </svg>
);

const HEARTS = [{ left: "12em", size: "3.4em", delay: 0 }, { left: "17.5em", size: "2.3em", delay: 0.3 }, { left: "8.8em", size: "2.1em", delay: 0.55 }];

const HeartBurst = () => (
  <>
    {HEARTS.map((h, i) => (
      <motion.div key={i} className="absolute" style={{ left: h.left, zIndex: 5 }}
        initial={{ bottom: "36em", opacity: 0, scale: 0.4 }}
        animate={{ bottom: "48em", opacity: [0, 1, 1, 0], scale: [0.4, 1, 1, 0.9] }}
        transition={{ duration: 2.8, delay: h.delay, ease: "easeOut", times: [0, 0.18, 0.7, 1] }}>
        <PixelHeart size={h.size} />
      </motion.div>
    ))}
  </>
);


const Chip = ({ children, className = "", testId }) => (
  <div data-testid={testId} className={`px-box px-box-dark px-[1em] py-[0.5em] flex items-center gap-[0.6em] ${className}`}>{children}</div>
);

const ReadCountdown = ({ lang, readMs, leftMs }) => {
  const total = Math.max(1, Math.round(readMs / 1000));
  const left = Math.ceil(leftMs / 1000);
  return (
    <div className="mt-[1.4em] flex items-center gap-[1.2em]">
      <div className="flex gap-[0.3em]">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className="w-[1.4em] h-[1em]" style={{ background: i < total - left ? "#1a1420" : "#d6d0b8" }} />
        ))}
      </div>
      <div className="font-silk text-[1.4em] text-[#5b4a2e]" data-testid="host-read-countdown">{tr(lang, "answers_in", { s: left })}</div>
    </div>
  );
};

export const DialogueScene = ({ game, customer, question, now, answered, playerCount, counts, reveal, lang, onNext }) => {
  const shown = reveal || now >= game.options_at;
  const text = useTypewriter(question.text, 26, sfx.blip);
  const secsLeft = Math.max(0, Math.ceil((game.ends_at - now) / 1000));
  const readMs = game.quiz.settings.read_time * 1000;
  const f = game.flat[game.idx];
  const total = counts.reduce((a, b) => a + b, 0);
  const correctCount = question.options.reduce((s, o, i) => s + (o.correct ? (counts[i] || 0) : 0), 0);
  const grumpy = reveal && playerCount > 0 && correctCount < playerCount * 0.5;
  const portraitSrc = grumpy ? grumpyPortrait(customer.type) : CUSTOMERS[customer.type].portrait;
  const perfect = reveal && playerCount > 0 && correctCount === playerCount;
  useEffect(() => { if (perfect) sfx.heart(); }, [perfect]);

  return (
    <div className="absolute inset-0" data-testid="host-dialogue-scene">
      <div className="store-bg" style={{ filter: "brightness(0.45) blur(3px)", transform: "scale(1.05)", ...(bgSrc(customer.background) ? { backgroundImage: `url(${bgSrc(customer.background)})` } : {}) }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(8,10,20,0.85), transparent 60%)" }} />
      <motion.img
        key={portraitSrc}
        src={portraitSrc}
        alt={customer.name}
        data-testid="host-customer-portrait"
        data-grumpy={grumpy ? "1" : "0"}
        className="pixelated absolute left-[-2em] bottom-0 h-[42em] w-[42em] object-contain"
        initial={{ x: "-8em", opacity: 0 }}
        animate={grumpy ? { x: 0, opacity: 1, rotate: [0, -1.6, 1.6, -1.1, 0.8, 0] } : { x: 0, opacity: 1, rotate: 0 }}
        transition={grumpy ? { rotate: { duration: 0.5, ease: "easeInOut" }, x: { duration: 0.4 }, opacity: { duration: 0.4 } } : { duration: 0.5, ease: "easeOut" }}
      />
      {perfect && <HeartBurst />}

      <div className="absolute left-[31em] right-[3em] top-[1.8em] flex items-center justify-between">
        <Chip testId="host-question-progress">
          <span className="font-silk text-[1.4em] text-[#FFD700]">{tr(lang, "customer_nr", { n: f.c + 1 })}</span>
          <span className="font-silk text-[1.4em] text-slate-300">· {tr(lang, "question_x_of_y", { n: game.idx + 1, t: game.flat.length })}</span>
        </Chip>
        <div className="flex items-center gap-[1.4em]">
          {shown && !reveal && (
            <>
              <Chip testId="host-answered-count">
                <span className="font-silk text-[1.3em]">{tr(lang, "answers_count")}</span>
                <span className="font-num text-[2em] leading-none">{answered}/{playerCount}</span>
              </Chip>
              <Chip className={secsLeft <= 5 ? "!bg-[#E21B3C]" : ""} testId="host-timer">
                <span className="font-num text-[2.8em] leading-none">{secsLeft}</span>
              </Chip>
            </>
          )}
          {reveal && (
            <button data-testid="host-game-next-question-button" className="px-btn text-[1.2em]" onClick={onNext}>{tr(lang, "next")} ▶</button>
          )}
        </div>
      </div>

      <div className={`absolute left-[31em] right-[3em] top-[7em] bottom-[3em] flex flex-col gap-[2em] ${shown ? "justify-start" : "justify-center"}`}>
        <motion.div layout transition={{ type: "spring", stiffness: 200, damping: 26 }} className="px-box relative px-[2em] pt-[2em] pb-[1.6em] mt-[1.2em]">
          <div className="absolute -top-[1.8em] left-[1.4em] px-box px-box-dark px-[0.8em] py-[0.3em]">
            <span className="font-silk text-[1.5em] text-[#FFD700]" data-testid="host-customer-name">{customer.name}</span>
          </div>
          <div className="font-pixel font-semibold leading-snug" data-testid="host-question-text" style={{ fontSize: shown ? "2.3em" : "3em" }}>
            {text}
            {text.length < question.text.length && <span className="blink">▌</span>}
          </div>
          {!shown && readMs > 0 && <ReadCountdown lang={lang} readMs={readMs} leftMs={game.options_at - now} />}
        </motion.div>
        {shown && (
          <div className="flex-1 grid grid-cols-2 gap-[1.4em] auto-rows-fr min-h-0" data-testid="host-answer-grid">
            {question.options.map((o, i) => (
              <AnswerTile key={i} i={i} text={o.text} reveal={reveal} correct={o.correct} count={counts[i] || 0} total={total} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
